import { PropsWithChildren, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { useValidationsStore } from '@/components/datamodel/hooks/useValidationsStore';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { logger } from '@/services/logger';
import { PersistedItem } from '@/types/DataModel';

import { useRequiredEditingRow } from './hooks/useEditingRow';

// Menus and popups that belong to the editing cell but render under
// document.body. A language option in the Dimensional select is one of
// these: Radix closes the menu on pointerup, then the click is delivered
// to whatever is underneath.
const PORTAL_SELECTOR = [
    '.select-content',
    '.select-item',
    '.react-datepicker',
    '.react-datepicker-popper',
    '[data-radix-popper-content-wrapper]',
    '[data-radix-portal]',
    '[data-radix-select-viewport]',
    '[role="listbox"]',
    '[role="option"]',
    '[role="dialog"]',
    '[role="menu"]',
    '[role="tooltip"]',
].join(', ');

const eventTargetElement = (target: EventTarget | null): Element | null => {
    if (target instanceof Element) return target;
    // The option label is often a text node, which has no closest().
    if (target instanceof Node) return target.parentElement;
    return null;
};

const eventHitsPortal = (event: Event): boolean => {
    const direct = eventTargetElement(event.target);
    if (direct?.closest(PORTAL_SELECTOR)) return true;
    return event.composedPath().some((node) =>
        node instanceof Element && Boolean(node.closest(PORTAL_SELECTOR)),
    );
};

interface EditableRowFormProviderProps {
    /** The row snapshot from React Query used to seed the RHF form. */
    row: PersistedItem;
    /**
     * Layout name whose sections are being edited (defaults to `browse`).
     * The datamodel components (Enum, etc.) read this via `useLayout()`
     * to resolve per-section attribute params.
     */
    layoutName?: string;
}

/**
 * Wraps the currently edited row's cells in a `FormProvider` seeded from
 * the row's React Query snapshot. Every RHF change is forwarded to the
 * three debounced queues via `enqueue`.
 *
 * Mounted from `ItemsList` only for the row whose `primaryKey__` matches
 * `editingRowId` (see `useEditingRow` + the `rowWrapper` prop on `Grid`),
 * so we don't pay the RHF cost on rows the user isn't editing.
 */
const EditableRowFormProvider = ({
    row,
    layoutName = 'browse',
    children,
}: PropsWithChildren<EditableRowFormProviderProps>) => {
    const {
        enqueue,
        discardEdit,
        commitEdit,
        editingAttributeName,
        scheduleLeave,
        cancelScheduledLeave,
        initialPatch,
        clearInitialPatch,
    } = useRequiredEditingRow();

    // RHF's `defaultValues` typing rejects unions with `undefined` at the
    // top level, which `PersistedItem` technically has via its optional
    // system fields even though the runtime value is a real row snapshot.
    // Fall back to `PersistedItem`'s `[key: string]: any` index signature.
    const methods = useForm({
        // A checkbox click both enters edit mode and writes the new value.
        // Seed that value so the editor's first paint already shows it.
        defaultValues: { ...row, ...(initialPatch ?? {}) },
        mode: 'onChange',
    });

    // Keep the latest `discardEdit` / `commitEdit` in refs so the document
    // listeners can stay stable across re-renders. Both callbacks change
    // on every render (their transitive `stopAll` dep is a fresh object
    // from `useItemChangesQueue`), so re-binding the DOM listeners each
    // render would open a small "no listener attached" window where a
    // key press or outside click could be missed.
    const discardEditRef = useRef(discardEdit);
    const commitEditRef = useRef(commitEdit);
    const scheduleLeaveRef = useRef(scheduleLeave);
    const cancelScheduledLeaveRef = useRef(cancelScheduledLeave);
    useEffect(() => {
        discardEditRef.current = discardEdit;
    }, [discardEdit]);
    useEffect(() => {
        commitEditRef.current = commitEdit;
    }, [commitEdit]);
    useEffect(() => {
        scheduleLeaveRef.current = scheduleLeave;
    }, [scheduleLeave]);
    useEffect(() => {
        cancelScheduledLeaveRef.current = cancelScheduledLeave;
    }, [cancelScheduledLeave]);

    // Anchor to the cell in edit mode (`data-editing-cell`), not the
    // whole row. LAX-10059 leaves edit mode when the user clicks outside
    // that one field. Re-queried when the active attribute changes so a
    // double-click into the next cell updates the anchor without
    // remounting the row form.
    const cellElementRef = useRef<HTMLElement | null>(null);
    // Radix Select opens on pointerdown and then sets pointer-events: none
    // on the page. The click that follows is retargeted onto <html>, outside
    // the cell, both when the language menu opens and when an option is
    // chosen. Those gestures must not leave edit mode.
    const pointerDownInCellRef = useRef(false);
    const pointerDownInPortalRef = useRef(false);
    useEffect(() => {
        cellElementRef.current = document.querySelector('[data-editing-cell="true"]');
    }, [editingAttributeName]);

    // Escape (capture) - discard pending changes and exit edit mode.
    // Enter (bubble) - commit pending changes and exit edit mode.
    //
    // The two phases differ intentionally: Escape must run before any
    // child (Combobox / react-select) can call `stopPropagation` on its
    // own Esc handler and swallow it, whereas Enter must let child
    // editors handle it first (a Combobox uses Enter to pick the
    // highlighted option) - if the child stops propagation, we don't
    // reach the bubble handler and we don't commit prematurely.
    useEffect(() => {
        const handleKeyDownCapture = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            discardEditRef.current();
        };
        const handleKeyDownBubble = (event: KeyboardEvent) => {
            if (event.key !== 'Enter') return;
            // IME composition emits Enter to finish composing; don't treat
            // that as "user finished editing the row".
            if (event.isComposing || event.keyCode === 229) return;
            // Shift/Ctrl/Alt/Meta+Enter often mean "newline" or app-level
            // shortcut - keep them out of the commit path.
            if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
                return;
            }
            const target = event.target as Node | null;
            const cell = cellElementRef.current;
            if (!target || !cell) return;
            // An open menu (language list, combobox, calendar) handles
            // Enter itself. Committing here would leave edit mode as soon
            // as the user picks an option from the keyboard.
            if (eventHitsPortal(event)) return;
            if (!cell.contains(target)) return;
            commitEditRef.current();
        };
        document.addEventListener('keydown', handleKeyDownCapture, true);
        document.addEventListener('keydown', handleKeyDownBubble, false);
        return () => {
            document.removeEventListener('keydown', handleKeyDownCapture, true);
            document.removeEventListener('keydown', handleKeyDownBubble, false);
        };
    }, []);

    // Click-outside the editing *cell*. A single click anywhere that is
    // not another editable field commits immediately. A click on another
    // editable field waits one double-click interval: the second click
    // (detail > 1) cancels the leave so `beginEdit` can switch cells,
    // and a lone click still leaves edit mode.
    useEffect(() => {
        let clearGestureTimer: number | null = null;
        const clearGestureSoon = () => {
            if (clearGestureTimer != null) window.clearTimeout(clearGestureTimer);
            // Radix preventDefault on pointerdown can swallow the click.
            // Drop the flags if it never arrives so a later outside click
            // still leaves edit mode.
            clearGestureTimer = window.setTimeout(() => {
                pointerDownInCellRef.current = false;
                pointerDownInPortalRef.current = false;
                clearGestureTimer = null;
            }, 50);
        };
        const handlePointerDown = (event: PointerEvent) => {
            const element = eventTargetElement(event.target);
            const cell = cellElementRef.current;
            pointerDownInCellRef.current = Boolean(cell && element && cell.contains(element));
            pointerDownInPortalRef.current = eventHitsPortal(event);
            if (pointerDownInCellRef.current || pointerDownInPortalRef.current) {
                clearGestureSoon();
            }
        };
        const handlePointerUp = (event: PointerEvent) => {
            if (!eventHitsPortal(event)) return;
            pointerDownInPortalRef.current = true;
            clearGestureSoon();
        };
        const handleClick = (event: MouseEvent) => {
            const startedInCell = pointerDownInCellRef.current;
            const startedInPortal = pointerDownInPortalRef.current;
            pointerDownInCellRef.current = false;
            pointerDownInPortalRef.current = false;
            if (clearGestureTimer != null) {
                window.clearTimeout(clearGestureTimer);
                clearGestureTimer = null;
            }
            const hitPortal = eventHitsPortal(event);
            // Opening or choosing a language retargets this click onto the
            // page. It is the same gesture as the press inside the editor.
            if (startedInCell || startedInPortal || hitPortal) {
                const element = eventTargetElement(event.target);
                const cell = cellElementRef.current;
                const stillInside = Boolean(
                    (cell && element && cell.contains(element)) || hitPortal,
                );
                if (!stillInside) event.stopPropagation();
                return;
            }
            const target = eventTargetElement(event.target);
            if (!target) return;
            const cell = cellElementRef.current;
            if (!cell) return;
            if (cell.contains(target)) return;
            if (target.closest(PORTAL_SELECTOR)) return;
            const nextEditable = target.closest('[data-hit-list-editable="true"]');
            if (nextEditable) {
                if (event.detail > 1) {
                    cancelScheduledLeaveRef.current();
                    return;
                }
                scheduleLeaveRef.current();
                return;
            }
            cancelScheduledLeaveRef.current();
            commitEditRef.current();
        };
        document.addEventListener('pointerdown', handlePointerDown, true);
        document.addEventListener('pointerup', handlePointerUp, true);
        document.addEventListener('click', handleClick, true);
        return () => {
            if (clearGestureTimer != null) window.clearTimeout(clearGestureTimer);
            document.removeEventListener('pointerdown', handlePointerDown, true);
            document.removeEventListener('pointerup', handlePointerUp, true);
            document.removeEventListener('click', handleClick, true);
        };
    }, []);

    // `setValue` does not emit a watch `type`, so a click-to-toggle patch
    // has to be enqueued here. The form may already be mounted when the
    // user clicks a checkbox in a row that is editing another cell.
    useEffect(() => {
        if (!initialPatch) return;
        Object.entries(initialPatch).forEach(([key, value]) => {
            methods.setValue(key, value, { shouldDirty: true, shouldTouch: true });
        });
        enqueue(initialPatch);
        clearInitialPatch();
    }, [initialPatch, methods, enqueue, clearInitialPatch]);

    useEffect(() => {
        const subscription = methods.watch((values, { name, type }) => {
            // Filter setValue-driven writes (no `type`) so we don't
            // ping-pong changes coming from remote validation / peer edits.
            if (!type || !name) return;

            const topLevelName = name.includes('.') ? name.split('.')[0] : name;
            const nextValue = (values as Record<string, unknown>)[topLevelName];
            const patch: Partial<PersistedItem> = {
                [topLevelName]: nextValue === undefined ? null : nextValue,
            };

            logger.debug(
                'EditableRowFormProvider: enqueueing patch',
                topLevelName,
                patch,
            );

            enqueue(patch);
        });

        return () => subscription.unsubscribe();
    }, [methods, enqueue]);

    // Clear the shared validation + attribute state stores when this row
    // leaves edit mode (either because the user switched to another row or
    // because `pauseEditing()` was called for a modal). Mirrors the
    // `clearErrors / clearWarnings / clearState` cleanup that the item
    // editor's `useValidations` does on unmount.
    useEffect(() => {
        return () => {
            const validations = useValidationsStore.getState();
            validations.clearErrors();
            validations.clearWarnings();
            validations.clearLocalValidationErrors();
            useAttributesStateStore.getState().clearState();
        };
    }, []);

    // Datamodel components read the current layout via `useLayout()` and the
    // reset-on-save signal via `useFormReset()`. Both providers normally live
    // on the item editor dialog; we mount narrow, row-scoped copies here so
    // Enum / Date / DateTime work identically inside the grid.
    return (
        <LayoutProvider layoutName={layoutName}>
            <FormResetProvider>
                <FormProvider {...methods}>{children}</FormProvider>
            </FormResetProvider>
        </LayoutProvider>
    );
};

export { EditableRowFormProvider };
